import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  DialogActions,
  Grid,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  Divider,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useAppNavigation } from '../hooks/useAppNavigation';
import api from '../services/api';
import boxService from '../services/boxService';
import { placeholderImage, getBestItemImageUrl } from '../utils/imageUtils';
import { getCachedBlobUrl, isInCache } from '../utils/imageCache';
import OfflineImage from './OfflineImage';

// Import styling from our design system
import { colors, shadows } from '../styles/styleConstants';
import { modalStyles } from '../styles/componentStyles';
import QRLabelPrint from './QRLabelPrint';
import AssignQRDialog from './AssignQRDialog';
import ItemDetailModal from './ItemDetailModal';

const BoxDetailModal = ({ open, onClose, boxId }) => {
  const navigate = useAppNavigation();
  const [box, setBox] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [boxItems, setBoxItems] = useState([]);
  const [sortOrder, setSortOrder] = useState('newest'); // Options: newest, oldest, nameAsc, nameDesc
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const [locationExpanded, setLocationExpanded] = useState(false); // For collapsible location section
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [qrLabelOpen, setQrLabelOpen] = useState(false);
  const [assignQrOpen, setAssignQrOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [itemDetailOpen, setItemDetailOpen] = useState(false);
  
  // Navigation is now handled by the parent component (FindItems)
  // Just call onClose which will trigger navigation in the parent

  // Define fetch function before useEffect that calls it
  const fetchBoxData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if device is offline
      const isActuallyOffline = !navigator.onLine;
      if (isActuallyOffline) {
        throw new Error('You are currently offline. Please check your internet connection and try again.');
      }
      
      let boxData = null;
      let boxItems = [];
      
      // Get from API (cache-busting will be handled automatically by api.js)
      console.log(`BoxDetailModal: Getting box ${boxId} from API`);
      const response = await api.get(`/boxes/${boxId}`);
      boxData = response.data;
      
      // Get items for this box - first try with query parameter
      try {
        console.log(`BoxDetailModal: Getting items for box ${boxId} with query parameter`);
        const itemsResponse = await api.get(`/items?box_id=${boxId}`);
        boxItems = Array.isArray(itemsResponse.data) ? itemsResponse.data : [];
        
        // Use clean image URLs without cache-busting (smart cache-busting handles this)
        boxItems = boxItems.map(item => ({
          ...item,
          // Use clean image URLs without cache-busting (smart cache-busting handles this)
          image_url_with_cache_buster: item.image_url ? getBestItemImageUrl(item) : null
        }));
      } catch (itemsError) {
        console.warn(`Error getting items with query parameter: ${itemsError.message}. Trying alternative endpoint...`);
        
        // Fallback to the boxService method which uses a consistent endpoint pattern
        try {
          const items = await boxService.getBoxItems(boxId);
          boxItems = Array.isArray(items) ? items : [];
          
          // Use clean image URLs without cache-busting (smart cache-busting handles this)
          boxItems = boxItems.map(item => ({
            ...item,
            // Use clean image URLs without cache-busting (smart cache-busting handles this)
            image_url_with_cache_buster: item.image_url ? getBestItemImageUrl(item) : null
          }));
        } catch (fallbackError) {
          console.error(`Error getting items from fallback endpoint: ${fallbackError.message}`);
          boxItems = []; // Default to empty array if both methods fail
        }
      }
      
      console.log(`BoxDetailModal: Box data loaded. Items count: ${boxItems.length}`);
      setBox(boxData);
      setBoxItems(boxItems);
    } catch (error) {
      console.error(`Error loading box data: ${error.message}`);
      setError(`Failed to load data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [boxId]);

  useEffect(() => {
    // Only fetch if the modal is open and we have a boxId
    if (open && boxId) {
      fetchBoxData();
    }
  }, [open, boxId, fetchBoxData]);

  // Reset when modal is closed
  useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const handleEditClick = () => {
    // Fix: Navigate to the correct path for editing boxes
    navigate.to.BOX_EDIT(boxId);
    onClose();
  };
  
  const handleViewFullBox = () => {
    navigate.to.BOX_DETAIL(boxId);
    onClose();
  };
  
  const handleAddItem = () => {
    // Navigate to add item page with box pre-selected
    navigate.to.ADD_ITEM();
    // Store the selected box ID in localStorage so AddItem page can pre-select it
    localStorage.setItem('preselected_box_id', boxId);
    onClose();
  };
  
  const handleDeleteClick = (item, event) => {
    // Stop propagation to prevent zoom
    event.stopPropagation();
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      await api.delete(`/items/${itemToDelete.id}`);
      
      // Remove item from local state
      setBoxItems(prevItems => prevItems.filter(item => item.id !== itemToDelete.id));
      
      // Close confirmation dialog
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      
      console.log(`Successfully deleted item ${itemToDelete.id}`);
    } catch (error) {
      console.error('Error deleting item:', error);
      setError(`Failed to delete item: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };
  
  // Handle item click - open item detail modal
  const handleItemClick = (item) => {
    console.log(`[BoxDetailModal] Opening item detail for item ${item.id}`);
    setSelectedItemId(item.id);
    setItemDetailOpen(true);
  };
  
  // Handle item detail modal close
  const handleItemDetailClose = () => {
    setItemDetailOpen(false);
    setSelectedItemId(null);
    // Refresh box data in case item was edited
    fetchBoxData();
  };
  
  // Close the zoom view
  const handleZoomClose = () => {
    setZoomOpen(false);
  };
  
  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };
  
  // Sort items based on the current sort order
  const getSortedItems = () => {
    if (!boxItems || !Array.isArray(boxItems)) return [];
    
    const items = [...boxItems]; // Create a copy to avoid mutating the original array
    
    switch (sortOrder) {
      case 'newest':
        return items.sort((a, b) => b.id - a.id); // Higher ID = newer
      case 'oldest':
        return items.sort((a, b) => a.id - b.id);
      case 'nameAsc':
        return items.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      case 'nameDesc':
        return items.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          if (nameA > nameB) return -1;
          if (nameA < nameB) return 1;
          return 0;
        });
      default:
        return items;
    }
  };

  // Detect if box contains new items uploaded in this session
  const newItemIds = useMemo(() => {
    try {
      // Check for cubxly_upload_success to see if we just uploaded items
      const uploadSuccessData = localStorage.getItem('cubxly_upload_success');
      if (uploadSuccessData) {
        const data = JSON.parse(uploadSuccessData);
        // Consider items uploaded within the last 60 seconds as "recent"
        const isRecent = Date.now() - data.timestamp < 60000;
        const matchesCurrentBox = String(data.boxId) === String(boxId);
        
        if (isRecent && matchesCurrentBox && Array.isArray(data.itemIds)) {
          console.log('BoxDetailModal: Detected recent uploads to this box:', data.itemIds);
          return new Set(data.itemIds);
        }
      }
      
      // Also check the dedicated new item IDs storage
      const newItemIdsData = localStorage.getItem('cubxly_new_item_ids');
      if (newItemIdsData) {
        try {
          const ids = JSON.parse(newItemIdsData);
          if (Array.isArray(ids) && ids.length > 0) {
            console.log('BoxDetailModal: Found new item IDs:', ids);
            return new Set(ids);
          }
        } catch (e) {
          console.warn('Error parsing new item IDs:', e);
        }
      }
      
      return new Set();
    } catch (e) {
      console.error('Error detecting new items:', e);
      return new Set();
    }
  }, [boxId]);

  // Memoize image URLs to prevent regeneration on every render
  const itemImageUrls = useMemo(() => {
    if (!boxItems || !Array.isArray(boxItems)) return {};
    
    const urls = {};
    boxItems.forEach(item => {
      // Use cache-busted URL if available (for newly added items), otherwise use standard URL
      const imageUrl = item.image_url_with_cache_buster || getBestItemImageUrl(item);
      urls[item.id] = imageUrl;
      
      // Log if this is a new item we're watching for
      if (newItemIds.has(item.id)) {
        console.log(`BoxDetailModal: Watching new item ${item.id} with URL ${imageUrl} (cache-busted: ${!!item.image_url_with_cache_buster})`);
      }
    });
    return urls;
  }, [boxItems, newItemIds]); // Recalculate when boxItems or recentUpload changes
  
  // Get sorted items - show each item individually (no grouping)
  const sortedItems = useMemo(() => {
    return getSortedItems();
  }, [boxItems, sortOrder]);
  
  const renderBoxDetails = () => {
    return (
      <Box>
        {box.description && (
          <Typography variant="body2" paragraph sx={{ mt: 0.5 }}>
            {box.description}
          </Typography>
        )}
        
        {/* Collapsible Location section */}
        {(box.location_full || box.location) && (
          <Box sx={{ mb: 1, mt: 1 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
              onClick={() => setLocationExpanded(!locationExpanded)}
            >
              <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 'bold' }}>
                📍 Location
              </Typography>
              <IconButton 
                edge="end" 
                size="small" 
                sx={{ p: 0 }}
                aria-expanded={locationExpanded}
                aria-label="show more"
              >
                {locationExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={locationExpanded}>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {box.location_full || box.location}
              </Typography>
            </Collapse>
          </Box>
        )}

        {box.tags && box.tags.length > 0 && (
          <Box sx={{ mt: 2.5, mb: 2.5 }}>
            <Typography 
              variant="subtitle2" 
              component="h3" 
              sx={{ 
                mb: 1, 
                fontWeight: 600,
                color: colors.darkSlateBlue,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <LocalOfferIcon fontSize="small" sx={{ color: colors.actionBlue }} />
              Tags
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {box.tags.map((tag, index) => (
                <Chip 
                  key={index} 
                  label={tag} 
                  size="small" 
                  sx={{ 
                    mb: 0.5,
                    mr: 0.5,
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    color: colors.mediumSlateBlue,
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                    '& .MuiChip-label': {
                      px: 1.5
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
        
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, mt: 2.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                color: colors.darkSlateBlue,
                fontFamily: "'Poppins', sans-serif"
              }}>
              Items in this Box ({boxItems.length})
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {boxItems.length > 1 && (
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120, maxHeight: 32 }}>
                  <Select
                    id="sort-items-select"
                    value={sortOrder}
                    onChange={handleSortChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Sort items' }}
                    sx={{ fontSize: '0.8rem', height: 32 }}
                  >
                    <MenuItem value="newest">Newest first</MenuItem>
                    <MenuItem value="oldest">Oldest first</MenuItem>
                    <MenuItem value="nameAsc">Name (A-Z)</MenuItem>
                    <MenuItem value="nameDesc">Name (Z-A)</MenuItem>
                  </Select>
                </FormControl>
              )}
              
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                sx={{
                  backgroundColor: colors.actionBlue,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  height: 32,
                  '&:hover': {
                    backgroundColor: colors.darkSlateBlue,
                    transform: 'translateY(-1px)',
                    boxShadow: shadows.sm,
                  }
                }}
              >
                Add Item
              </Button>
            </Box>
          </Box>
          
          {boxItems && boxItems.length > 0 ? (
            <Box 
              sx={{ 
                maxHeight: window.innerWidth < 600 ? 'calc(100vh - 220px)' : '400px', // Taller on mobile
                overflowY: 'auto',
                pr: 1.5,
                pt: 1,
                // Add custom scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(94, 108, 132, 0.05)',
                  borderRadius: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(94, 108, 132, 0.2)',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(94, 108, 132, 0.3)',
                  },
                },
                mt: 1.5
              }}
            >
              <Grid container spacing={1}>
                {sortedItems.map((item) => (
                  <Grid item xs={4} sm={4} md={4} key={item.id}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        cursor: 'zoom-in',
                        position: 'relative',
                        transition: 'all 0.2s ease-in-out',
                        borderRadius: 2,
                        overflow: 'visible',
                        boxShadow: shadows.sm,
                        '&:hover': {
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: shadows.md,
                          '& .delete-button': {
                            opacity: 1
                          }
                        }
                      }}
                      onClick={() => handleItemClick(item)}
                    >
                      <Box sx={{ position: 'relative', paddingTop: '100%', overflow: 'hidden', borderRadius: 2 }}>
                        <OfflineImage
                          src={itemImageUrls[item.id]}
                          itemId={item.id}
                          alt={item.name || 'Item'}
                          fallbackSrc={placeholderImage}
                          prefetch={false}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        {/* Delete button overlay - desktop only */}
                        <IconButton
                          className="delete-button"
                          size="small"
                          onClick={(event) => handleDeleteClick(item, event)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            opacity: 0,
                            display: { xs: 'none', sm: 'flex' },
                            transition: 'all 0.2s',
                            zIndex: 10,
                            boxShadow: shadows.sm,
                            '&:hover': {
                              backgroundColor: 'rgba(220, 38, 38, 1)',
                              transform: 'scale(1.1)',
                              boxShadow: shadows.md
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {/* Zoom indicator overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(61, 72, 90, 0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            backdropFilter: 'blur(2px)',
                            pointerEvents: 'none',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        >
                          <ZoomInIcon sx={{ fontSize: 30, color: 'white' }} />
                        </Box>
                      </Box>
                      <Box sx={{ 
                        py: 0.75, 
                        px: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        gap: 0.5
                      }}>
                        <Typography 
                          variant="caption" 
                          noWrap 
                          sx={{ 
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            color: colors.darkSlateBlue,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {item.name || 'Unnamed Item'}
                        </Typography>
                        {/* Delete button for mobile - in caption area */}
                        <IconButton
                          size="small"
                          onClick={(event) => handleDeleteClick(item, event)}
                          sx={{
                            display: { xs: 'flex', sm: 'none' },
                            p: 0.5,
                            minWidth: 'auto',
                            color: colors.danger,
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              color: '#dc2626'
                            },
                            '&:active': {
                              transform: 'scale(0.9)'
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <InfoIcon sx={{ fontSize: 48, color: colors.mediumSlateBlue, mb: 2, opacity: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This box doesn't have any items yet.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                sx={{
                  backgroundColor: colors.actionBlue,
                  borderRadius: 2,
                  px: 3,
                  '&:hover': {
                    backgroundColor: colors.darkSlateBlue,
                    transform: 'translateY(-2px)',
                    boxShadow: shadows.md,
                  }
                }}
              >
                Add First Item
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {/* Main Box Detail Modal */}
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600} 
        aria-labelledby="box-detail-dialog-title"
        PaperProps={{
          sx: modalStyles.paper
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {/* Enhanced header */}
        <DialogTitle sx={{ 
          ...modalStyles.title,
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          mb: 1
        }}>
          <Typography 
            variant="h5" 
            component="div" 
            noWrap 
            sx={{ 
              maxWidth: { xs: '70%', sm: '80%' }, 
              fontWeight: 600,
              fontFamily: "'Poppins', sans-serif",
              color: colors.darkSlateBlue
            }}
          >
            {box?.name || 'Box Details'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: colors.mediumSlateBlue,
              '&:hover': {
                backgroundColor: 'rgba(94, 108, 132, 0.08)',
                color: colors.darkSlateBlue,
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        {/* Content area with improved styling */}
        <DialogContent sx={{ 
          px: { xs: 2, sm: 3 }, 
          py: 2, 
          maxHeight: 'calc(100vh - 120px)',
          ...modalStyles.content 
        }}>
          {isLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              py: 6,
              height: '300px'
            }}>
              <CircularProgress sx={{ color: colors.actionBlue }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ 
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: colors.danger
              } 
            }}>
              {error}
            </Alert>
          ) : box ? (
            <Fade in={!isLoading}>
              <Box>{renderBoxDetails()}</Box>
            </Fade>
          ) : (
            <Alert severity="warning" sx={{ 
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: colors.warning
              } 
            }}>
              Box not found
            </Alert>
          )}
        </DialogContent>
        
        <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />
        
        {/* Enhanced buttons with better styling */}
        <DialogActions sx={{ 
          justifyContent: 'space-between', 
          py: 2.5, 
          px: 3,
          ...modalStyles.actions 
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={handleEditClick} 
              startIcon={<EditIcon />}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 2,
                color: colors.mediumSlateBlue,
                borderColor: colors.mediumSlateBlue,
                '&:hover': {
                  borderColor: colors.darkSlateBlue,
                  backgroundColor: 'rgba(94, 108, 132, 0.08)',
                }
              }}
            >
              Edit
            </Button>
            <Button 
              onClick={() => setQrLabelOpen(true)} 
              startIcon={<QrCodeIcon />}
              variant="outlined"
              disabled={!box?.uuid}
              title={!box?.uuid ? 'Assign a QR code first' : ''}
              sx={{
                borderRadius: 2,
                px: 2,
                color: colors.mediumSlateBlue,
                borderColor: colors.mediumSlateBlue,
                '&:hover': {
                  borderColor: colors.darkSlateBlue,
                  backgroundColor: 'rgba(94, 108, 132, 0.08)',
                }
              }}
            >
              QR Label
            </Button>
            <Button 
              onClick={() => setAssignQrOpen(true)} 
              startIcon={<QrCodeScannerIcon />}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 2,
                color: colors.mediumSlateBlue,
                borderColor: colors.mediumSlateBlue,
                '&:hover': {
                  borderColor: colors.darkSlateBlue,
                  backgroundColor: 'rgba(94, 108, 132, 0.08)',
                }
              }}
            >
              {box?.uuid ? 'Manage QR' : 'Add QR'}
            </Button>
          </Box>
          <Button 
            onClick={handleViewFullBox}
            variant="contained" 
            endIcon={<NavigateNextIcon />}
            sx={{
              backgroundColor: colors.actionBlue,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                backgroundColor: colors.actionBlue,
                transform: 'translateY(-2px)',
                boxShadow: shadows.md,
              }
            }}
          >
            View Full Box
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Image Zoom Dialog */}
      <Dialog
        open={zoomOpen}
        onClose={handleZoomClose}
        maxWidth="xl"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{ 
          sx: { 
            bgcolor: colors.darkSlateBlue,
            maxHeight: '95vh',
            borderRadius: 2,
            boxShadow: shadows.xl,
            overflow: 'hidden'
          } 
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={handleZoomClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 10,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <DialogContent 
            sx={{ 
              p: 2, 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'transparent',
              minHeight: '60vh'
            }}
          >
            {zoomImageUrl ? (
              <Box
                component="img"
                src={zoomImageUrl}
                alt="Zoomed item"
                crossOrigin="anonymous"
                sx={{
                  // Scale small images up to a minimum size while respecting max bounds
                  minWidth: { xs: '80vw', sm: '60vw', md: '50vw' },
                  minHeight: { xs: '40vh', sm: '50vh', md: '60vh' },
                  maxWidth: '95vw',
                  maxHeight: 'calc(95vh - 64px)',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                }}
              />
            ) : (
              <Box sx={{ p: 6, color: colors.lightGray, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                  No image available
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: shadows.xl
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 1,
          color: colors.danger,
          fontWeight: 600
        }}>
          <DeleteIcon />
          Delete Item?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{itemToDelete?.name || 'this item'}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            sx={{
              color: colors.mediumSlateBlue,
              '&:hover': {
                backgroundColor: 'rgba(94, 108, 132, 0.08)',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{
              backgroundColor: colors.danger,
              '&:hover': {
                backgroundColor: '#dc2626',
              }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* QR Label Print Dialog */}
      <QRLabelPrint
        open={qrLabelOpen}
        onClose={() => setQrLabelOpen(false)}
        boxId={boxId}
        boxName={box?.name}
      />
      
      {/* Assign/Replace QR Code Dialog */}
      <AssignQRDialog
        open={assignQrOpen}
        onClose={() => setAssignQrOpen(false)}
        boxId={boxId}
        boxName={box?.name}
        currentUuid={box?.uuid}
        onSuccess={(updatedBox) => {
          setBox(prev => ({ ...prev, uuid: updatedBox.uuid }));
          setAssignQrOpen(false);
        }}
      />
      
      {/* Item Detail Modal */}
      <ItemDetailModal
        open={itemDetailOpen}
        onClose={handleItemDetailClose}
        itemId={selectedItemId}
      />
    </>
  );
};

export default BoxDetailModal;
